import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import type { Language } from '@/types';
import {
  createQCReportsBatch,
  fetchLatestVisit,
  fetchVisitBlocks,
  type QCVisitBlock,
  type QCVisitOption,
} from '../api';

interface QCReportFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteName: string;
  token: string | null;
  language: Language;
  onCreated: () => void;
}

type Score = 1 | 2 | 3 | 4 | 5;

type BlockEntry = {
  block: QCVisitBlock;
  score: Score | null;
  notes: string;
};

const copy = {
  en: {
    title: 'Conduct QC',
    description: 'Score each block. Notes required when score is 1.',
    loading: 'Loading visit…',
    noVisit: 'No visits to inspect yet.',
    visitSummary: 'Inspecting visit',
    cleaner: 'Cleaner',
    blocksHeader: 'Blocks',
    extras: 'extra',
    recurring: 'recurring',
    photos: 'photos',
    points: 'pts',
    notesPlaceholder: 'Notes (required when 1 star)',
    notesRequired: 'Notes are required when score is 1.',
    extrasCheck: 'Inspected extra work',
    average: 'Average',
    cancel: 'Cancel',
    submit: 'Submit',
    submitting: 'Submitting…',
    rated: 'rated',
    submitErrorPrefix: 'Failed:',
    none: 'No blocks on this visit.',
  },
  sv: {
    title: 'Genomför QC',
    description: 'Betygsätt varje moment. Anteckning krävs vid 1 stjärna.',
    loading: 'Laddar besök…',
    noVisit: 'Inga besök att inspektera ännu.',
    visitSummary: 'Inspekterar besök',
    cleaner: 'Städare',
    blocksHeader: 'Moment',
    extras: 'extra',
    recurring: 'ordinarie',
    photos: 'bilder',
    points: 'p',
    notesPlaceholder: 'Anteckning (krävs vid 1 stjärna)',
    notesRequired: 'Anteckning krävs vid 1 stjärna.',
    extrasCheck: 'Inspekterade extrastäd',
    average: 'Snitt',
    cancel: 'Avbryt',
    submit: 'Skicka in',
    submitting: 'Skickar…',
    rated: 'betygsatta',
    submitErrorPrefix: 'Misslyckades:',
    none: 'Inga moment för detta besök.',
  },
} as const;

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline' {
  if (status === 'completed' || status === 'approved') return 'success';
  if (status === 'cancelled') return 'destructive';
  if (status === 'skipped') return 'warning';
  if (status === 'assigned') return 'secondary';
  if (status === 'planned') return 'outline';
  return 'default';
}

function StarPicker({
  value,
  onChange,
  disabled,
}: {
  value: Score | null;
  onChange: (next: Score) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value != null && n <= value;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n as Score)}
            aria-label={`${n} / 5`}
            className="p-0.5 transition-colors disabled:cursor-not-allowed"
          >
            <Star
              className={
                filled
                  ? 'h-6 w-6 fill-primary text-primary'
                  : 'h-6 w-6 text-muted-foreground/40 hover:text-muted-foreground'
              }
            />
          </button>
        );
      })}
    </div>
  );
}

export function QCReportFormDialog({
  open,
  onOpenChange,
  siteId,
  siteName,
  token,
  language,
  onCreated,
}: QCReportFormDialogProps) {
  const text = copy[language];
  const [visit, setVisit] = useState<QCVisitOption | null>(null);
  const [entries, setEntries] = useState<BlockEntry[]>([]);
  const [coversExtras, setCoversExtras] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Load visit + blocks whenever dialog opens.
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setVisit(null);
    setEntries([]);
    setCoversExtras(false);
    setSubmitError(null);

    (async () => {
      try {
        const v = await fetchLatestVisit(token, siteId);
        if (cancelled) return;
        setVisit(v);
        if (!v) {
          setIsLoading(false);
          return;
        }
        const blocks = await fetchVisitBlocks(token, v.job_id);
        if (cancelled) return;
        setEntries(blocks.map((b) => ({ block: b, score: null, notes: '' })));
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load visit');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, siteId, token]);

  // Reset scroll when content loads.
  useEffect(() => {
    if (!open || isLoading) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [open, isLoading]);

  const ratedEntries = useMemo(
    () => entries.filter((e) => e.score != null),
    [entries],
  );
  const average = useMemo(() => {
    if (ratedEntries.length === 0) return null;
    const sum = ratedEntries.reduce((acc, e) => acc + (e.score ?? 0), 0);
    return sum / ratedEntries.length;
  }, [ratedEntries]);

  const blockingMissingNote = entries.some(
    (e) => e.score === 1 && e.notes.trim().length === 0,
  );
  const canSubmit =
    !isSubmitting
    && !!visit
    && !!token
    && ratedEntries.length > 0
    && !blockingMissingNote;

  const updateEntry = (index: number, patch: Partial<Omit<BlockEntry, 'block'>>) => {
    setEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!token || !visit || ratedEntries.length === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createQCReportsBatch(token, {
        site_id: siteId,
        job_id: visit.job_id,
        covers_extras: coversExtras,
        scores: ratedEntries.map((e) => ({
          job_block_id: e.block.id,
          score: e.score as Score,
          notes: e.notes.trim() ? e.notes.trim() : null,
        })),
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{text.title}</DialogTitle>
          <DialogDescription>
            {siteName} · {text.description}
          </DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {text.loading}
            </div>
          ) : loadError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {loadError}
            </div>
          ) : !visit ? (
            <div className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
              {text.noVisit}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {text.visitSummary}
                </div>
                <div className="mt-1 font-medium">
                  {visit.scheduled_for} · {visit.cleaner_name ?? (language === 'sv' ? 'Ej tilldelad' : 'Unassigned')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {entries.length} {text.blocksHeader.toLowerCase()}
                  {visit.extras_count > 0 && ` · ${visit.extras_count} ${text.extras}`}
                </div>
              </div>

              {entries.length === 0 ? (
                <div className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                  {text.none}
                </div>
              ) : (
                <div className="space-y-3">
                  {entries.map((entry, index) => {
                    const { block, score, notes } = entry;
                    const needsNotes = score === 1 && notes.trim().length === 0;
                    return (
                      <div key={block.id} className="rounded-md border p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="font-medium">
                              {block.source_code ? `${block.source_code} · ` : ''}
                              {block.area ?? ''}
                            </div>
                            <div className="text-sm text-muted-foreground">{block.label}</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={statusBadgeVariant(block.status)}>{block.status}</Badge>
                            {block.points != null && (
                              <Badge variant="outline">
                                {Number(block.points).toFixed(2)} {text.points}
                              </Badge>
                            )}
                            <Badge variant="outline">
                              {block.photo_count}/{block.required_photos} {text.photos}
                            </Badge>
                            {block.job_kind === 'extra' && (
                              <Badge variant="secondary">{text.extras}</Badge>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 space-y-2">
                          <StarPicker
                            value={score}
                            onChange={(next) => updateEntry(index, { score: next })}
                            disabled={isSubmitting}
                          />
                          <Textarea
                            value={notes}
                            onChange={(e) => updateEntry(index, { notes: e.target.value })}
                            rows={2}
                            placeholder={text.notesPlaceholder}
                            disabled={isSubmitting}
                          />
                          {needsNotes && (
                            <p className="text-xs text-destructive">{text.notesRequired}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {visit.extras_count > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={coversExtras}
                    onCheckedChange={(value) => setCoversExtras(value === true)}
                    disabled={isSubmitting}
                  />
                  <span>{text.extrasCheck}</span>
                </label>
              )}

              {submitError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {text.submitErrorPrefix} {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t bg-background px-6 py-4">
          <div className="mr-auto flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              {ratedEntries.length}/{entries.length} {text.rated}
            </span>
            {average != null && (
              <span className="font-medium">
                {text.average}: {average.toFixed(2)}
              </span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {text.cancel}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            <span className={isSubmitting ? 'ml-1' : ''}>
              {isSubmitting ? text.submitting : text.submit}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
